import React, { useState, useEffect } from 'react';
import { productService } from '../services/productService';
import { translationService } from '../services/translationService';
import { getLocalizedContent } from '../utils/multilingual';
import { safeRender } from '../utils/safeRender';
import { 
  Edit, 
  X, 
  Trash2, 
  Plus, 
  FileText, 
  Image, 
  Palette, 
  Sparkles, 
  Ruler, 
  DollarSign, 
  Triangle,
  Globe,
  Camera,
  Upload,
  GripVertical,
  AlertCircle,
  Loader2,
  Languages,
  Minus
} from 'lucide-react';
import './ProductManager.css';

const ProductManager = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'add', 'edit'
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [catalogPdfFile, setCatalogPdfFile] = useState(null);
  const [uploadingCatalog, setUploadingCatalog] = useState(null);
  const [translatingProducts, setTranslatingProducts] = useState(new Set());
  const [currentLanguage, setCurrentLanguage] = useState('SR'); // SR, EN, DE

  // Form state - now multilingual
  const [formData, setFormData] = useState({
    title: { sr: '', en: '', de: '' },
    description: { sr: '', en: '', de: '' },
    catalog: {
      catalogNumber: '',
      tags: { sr: [], en: [], de: [] }
    },
    colors: [],
    sizes: [],
    price: {
      amount: '',
      currency: 'RSD'
    },
    availability: {
      inStock: true,
      quantity: 0
    },
    measurementGroups: [],
    plastificationTypes: {
      sjajna: false,
      matt: false,
      strukturalna: false,
      showOnProduct: false
    }
  });

  const [images, setImages] = useState([]);
  const [currentColor, setCurrentColor] = useState({
    name: { sr: '', en: '', de: '' },
    hexCode: '#000000',
    category: 'Aloksaza'
  });
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [currentSize, setCurrentSize] = useState({ name: '', code: '' });
  const [currentMeasurementGroup, setCurrentMeasurementGroup] = useState({
    groupTitle: { sr: '', en: '', de: '' },
    measurements: []
  });
  const [currentMeasurement, setCurrentMeasurement] = useState({
    name: { sr: '', en: '', de: '' },
    value: ''
  });
  const [editingGroupIndex, setEditingGroupIndex] = useState(null);
  const [editingMeasurementIndex, setEditingMeasurementIndex] = useState(null);
  const [activeGroupForMeasurement, setActiveGroupForMeasurement] = useState(null);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);


  useEffect(() => {
    if (activeTab === 'list') {
      fetchProducts();
    }
  }, [activeTab, showHidden]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await productService.getAllProductsForAdmin({ 
        limit: 50, 
        includeHidden: showHidden.toString() 
      }, token);
      setProducts(response.data.products || []);
    } catch (err) {
      setError('Greška pri učitavanju proizvoda');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const fieldParts = field.split('.');
      if (fieldParts.length === 2) {
        const [parent, child] = fieldParts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // New multilingual input handler
  const handleMultilingualInputChange = (field, value, language = currentLanguage) => {
    const langCode = language.toLowerCase();
    
    if (field.includes('.')) {
      const fieldParts = field.split('.');
      if (fieldParts.length === 2) {
        const [parent, child] = fieldParts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [langCode]: value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [langCode]: value
        }
      }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
  };

  const addColor = () => {
    const hasValidName = currentColor.name.sr || currentColor.name.en || currentColor.name.de;
    if (hasValidName && currentColor.hexCode && currentColor.category) {
      if (editingColorIndex !== null) {
        // Update existing color
        setFormData(prev => ({
          ...prev,
          colors: prev.colors.map((color, index) => 
            index === editingColorIndex ? { ...currentColor, available: true } : color
          )
        }));
        setEditingColorIndex(null);
      } else {
        // Add new color
        setFormData(prev => ({
          ...prev,
          colors: [...prev.colors, { ...currentColor, available: true }]
        }));
      }
      setCurrentColor({ 
        name: { sr: '', en: '', de: '' }, 
        hexCode: '#000000', 
 
      });
    }
  };

  const removeColor = (index) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter((_, i) => i !== index)
    }));
    // If we were editing this color, cancel editing
    if (editingColorIndex === index) {
      setEditingColorIndex(null);
      setCurrentColor({ 
        name: { sr: '', en: '', de: '' }, 
        hexCode: '#000000', 
 
      });
    }
  };

  const editColor = (index) => {
    const colorToEdit = formData.colors[index];
    setCurrentColor({ ...colorToEdit });
    setEditingColorIndex(index);
  };

  const cancelEditColor = () => {
    setEditingColorIndex(null);
    setCurrentColor({ 
      name: { sr: '', en: '', de: '' }, 
      hexCode: '#000000', 
      category: 'Aloksaza' 
    });
  };

  const addSize = () => {
    if (currentSize.name && currentSize.code) {
      setFormData(prev => ({
        ...prev,
        sizes: [...prev.sizes, { ...currentSize, available: true }]
      }));
      setCurrentSize({ name: '', code: '' });
    }
  };

  const removeSize = (index) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.filter((_, i) => i !== index)
    }));
  };

  // Measurement Group functions
  const addMeasurementGroup = () => {
    const hasValidTitle = currentMeasurementGroup.groupTitle.sr || currentMeasurementGroup.groupTitle.en || currentMeasurementGroup.groupTitle.de;
    
    if (hasValidTitle) {
      if (editingGroupIndex !== null) {
        // Update existing group
        setFormData(prev => ({
          ...prev,
          measurementGroups: prev.measurementGroups.map((group, index) => 
            index === editingGroupIndex ? { ...currentMeasurementGroup } : group
          )
        }));
        setEditingGroupIndex(null);
      } else {
        // Add new group
        setFormData(prev => ({
          ...prev,
          measurementGroups: [...prev.measurementGroups, { ...currentMeasurementGroup }]
        }));
      }
      setCurrentMeasurementGroup({
        groupTitle: { sr: '', en: '', de: '' },
        measurements: []
      });
      setActiveGroupForMeasurement(null);
    }
  };

  const removeMeasurementGroup = (groupIndex) => {
    setFormData(prev => ({
      ...prev,
      measurementGroups: prev.measurementGroups.filter((_, i) => i !== groupIndex)
    }));
    if (editingGroupIndex === groupIndex) {
      setEditingGroupIndex(null);
      setCurrentMeasurementGroup({
        groupTitle: { sr: '', en: '', de: '' },
        measurements: []
      });
    }
    if (activeGroupForMeasurement === groupIndex) {
      setActiveGroupForMeasurement(null);
    }
  };

  const editMeasurementGroup = (groupIndex) => {
    const groupToEdit = formData.measurementGroups[groupIndex];
    setCurrentMeasurementGroup({ ...groupToEdit });
    setEditingGroupIndex(groupIndex);
    setActiveGroupForMeasurement(groupIndex);
  };

  const cancelEditMeasurementGroup = () => {
    setEditingGroupIndex(null);
    setCurrentMeasurementGroup({
      groupTitle: { sr: '', en: '', de: '' },
      measurements: []
    });
    setActiveGroupForMeasurement(null);
  };

  // Individual measurement functions within a group
  const addMeasurementToGroup = (groupIndex) => {
    const hasValidName = currentMeasurement.name.sr || currentMeasurement.name.en || currentMeasurement.name.de;
    const hasValidValue = currentMeasurement.value && currentMeasurement.value.trim();
    
    if (hasValidName && hasValidValue) {
      if (editingMeasurementIndex !== null) {
        // Update existing measurement in group
        setFormData(prev => ({
          ...prev,
          measurementGroups: prev.measurementGroups.map((group, gIndex) => 
            gIndex === groupIndex ? {
              ...group,
              measurements: group.measurements.map((measurement, mIndex) => 
                mIndex === editingMeasurementIndex ? { ...currentMeasurement } : measurement
              )
            } : group
          )
        }));
        setEditingMeasurementIndex(null);
      } else {
        // Add new measurement to group
        setFormData(prev => ({
          ...prev,
          measurementGroups: prev.measurementGroups.map((group, gIndex) => 
            gIndex === groupIndex ? {
              ...group,
              measurements: [...group.measurements, { ...currentMeasurement }]
            } : group
          )
        }));
      }
      setCurrentMeasurement({
        name: { sr: '', en: '', de: '' },
        value: ''
      });
    }
  };

  const removeMeasurementFromGroup = (groupIndex, measurementIndex) => {
    setFormData(prev => ({
      ...prev,
      measurementGroups: prev.measurementGroups.map((group, gIndex) => 
        gIndex === groupIndex ? {
          ...group,
          measurements: group.measurements.filter((_, mIndex) => mIndex !== measurementIndex)
        } : group
      )
    }));
    
    if (editingMeasurementIndex === measurementIndex && activeGroupForMeasurement === groupIndex) {
      setEditingMeasurementIndex(null);
      setCurrentMeasurement({
        name: { sr: '', en: '', de: '' },
        value: ''
      });
    }
  };

  const editMeasurementInGroup = (groupIndex, measurementIndex) => {
    const measurementToEdit = formData.measurementGroups[groupIndex].measurements[measurementIndex];
    // Handle multilingual values
    const valueToEdit = typeof measurementToEdit.value === 'object' ? 
      measurementToEdit.value.sr || '' : 
      measurementToEdit.value || '';
    
    setCurrentMeasurement({
      name: { ...measurementToEdit.name },
      value: valueToEdit
    });
    setEditingMeasurementIndex(measurementIndex);
    setActiveGroupForMeasurement(groupIndex);
  };

  const cancelEditMeasurement = () => {
    setEditingMeasurementIndex(null);
    setCurrentMeasurement({
      name: { sr: '', en: '', de: '' },
      value: ''
    });
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    const langCode = currentLanguage.toLowerCase();
    setFormData(prev => ({
      ...prev,
      catalog: { 
        ...prev.catalog, 
        tags: {
          ...prev.catalog.tags,
          [langCode]: tags
        }
      }
    }));
  };

  const validateForm = () => {
    // Check if at least Serbian version is filled (as it's the default language)
    if (!formData.title.sr?.trim()) return 'Naziv proizvoda na srpskom je obavezan';
    if (!formData.description.sr?.trim()) return 'Opis proizvoda na srpskom je obavezan';
    if (!formData.catalog.catalogNumber.trim()) return 'Kataloski broj je obavezan';
    if (!formData.price.amount || formData.price.amount <= 0) return 'Cena mora biti veća od 0';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();
      
      // Add form data - now multilingual
      submitData.append('title', JSON.stringify(formData.title));
      submitData.append('description', JSON.stringify(formData.description));
      submitData.append('catalog', JSON.stringify(formData.catalog));
      submitData.append('colors', JSON.stringify(formData.colors));
      submitData.append('sizes', JSON.stringify(formData.sizes));
      submitData.append('price', JSON.stringify(formData.price));
      submitData.append('availability', JSON.stringify(formData.availability));
      submitData.append('measurementGroups', JSON.stringify(formData.measurementGroups));
      submitData.append('plastificationTypes', JSON.stringify(formData.plastificationTypes));
      
      // Debug logs
      console.log('FormData before sending:', {
        title: formData.title,
        description: formData.description,
        catalog: formData.catalog
      });

      // Add images
      images.forEach((image) => {
        submitData.append('images', image);
      });

      const token = localStorage.getItem('adminToken');
      
      if (editingProduct) {
        await productService.updateProduct(editingProduct._id, submitData, token);
      } else {
        await productService.createProduct(submitData, token);
      }

      // Reset form
      resetForm();
      setActiveTab('list');
      fetchProducts();
      
    } catch (err) {
      setError(err.message || 'Greška pri snimavanju proizvoda');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: { sr: '', en: '', de: '' },
      description: { sr: '', en: '', de: '' },
      catalog: {
        catalogNumber: '',
        category: { sr: '', en: '', de: '' },
        subcategory: { sr: '', en: '', de: '' },
        tags: { sr: [], en: [], de: [] }
      },
      colors: [],
      sizes: [],
      price: {
        amount: '',
        currency: 'RSD'
      },
      availability: {
        inStock: true,
        quantity: 0
      },
      measurementGroups: [],
      plastificationTypes: {
        sjajna: false,
        matt: false,
        strukturalna: false,
        showOnProduct: false
      }
    });
    setImages([]);
    setEditingProduct(null);
    setCurrentLanguage('SR');
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    
    // Helper function to ensure multilingual structure
    const ensureMultilingual = (field, fallback = '') => {
      if (typeof field === 'string') {
        return { sr: field, en: fallback, de: fallback };
      }
      return field || { sr: fallback, en: fallback, de: fallback };
    };
    
    const ensureMultilingualArray = (field) => {
      if (Array.isArray(field)) {
        return { sr: field, en: [], de: [] };
      }
      return field || { sr: [], en: [], de: [] };
    };
    
    setFormData({
      title: ensureMultilingual(product.title),
      description: ensureMultilingual(product.description),
      catalog: {
        catalogNumber: product.catalog?.catalogNumber || '',
        category: ensureMultilingual(product.catalog?.category),
        subcategory: ensureMultilingual(product.catalog?.subcategory),
        tags: ensureMultilingualArray(product.catalog?.tags)
      },
      colors: product.colors || [],
      sizes: product.sizes || [],
      price: product.price,
      availability: product.availability,
      measurementGroups: product.measurementGroups || [],
      plastificationTypes: product.plastificationTypes || {
        sjajna: false,
        matt: false,
        strukturalna: false,
        showOnProduct: false
      }
    });
    setActiveTab('add');
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj proizvod?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await productService.deleteProduct(productId, token);
      fetchProducts();
    } catch (err) {
      setError('Greška pri brisanju proizvoda');
    }
  };

  const handleHideProduct = async (productId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await productService.hideProduct(productId, token);
      fetchProducts();
    } catch (err) {
      setError('Greška pri sakrivanju proizvoda');
    }
  };

  const handleShowProduct = async (productId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await productService.showProduct(productId, token);
      fetchProducts();
    } catch (err) {
      setError('Greška pri prikazivanju proizvoda');
    }
  };

  const handleCatalogUpload = async (productId, file) => {
    if (!file) return;

    console.log('Starting catalog upload:', { productId, fileName: file.name, fileType: file.type });
    
    setUploadingCatalog(productId);
    try {
      const token = localStorage.getItem('adminToken');
      const result = await productService.uploadCatalogPdf(productId, file, token);
      console.log('Upload successful:', result);
      fetchProducts();
    } catch (err) {
      console.error('Upload error:', err);
      setError('Greška pri otpremanju kataloga: ' + err.message);
    } finally {
      setUploadingCatalog(null);
    }
  };

  const handleCatalogDelete = async (productId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete katalog?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await productService.deleteCatalogPdf(productId, token);
      fetchProducts();
    } catch (err) {
      setError('Greška pri brisanju kataloga');
    }
  };

  const handleTranslateProduct = async (productId) => {
    if (!window.confirm('Da li ste sigurni da želite da prevedete ovaj proizvod na engleski i nemački jezik pomoću AI?')) {
      return;
    }

    setTranslatingProducts(prev => new Set(prev.add(productId)));
    
    try {
      const token = localStorage.getItem('adminToken');
      await translationService.translateProduct(productId, ['en', 'de'], token);
      setError(null);
      alert('Proizvod je uspešno preveden!');
      fetchProducts(); // Refresh the list
    } catch (err) {
      console.error('Translation error:', err);
      setError('Greška pri prevođenju proizvoda: ' + err.message);
    } finally {
      setTranslatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handleImageColorAssociation = async (imageIndex, colorName) => {
    if (!editingProduct) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await productService.associateImageWithColor(
        editingProduct._id, 
        imageIndex, 
        colorName || null, 
        token
      );
      
      // Update the editing product with new data
      setEditingProduct(response.data);
      
      // Show success message (optional)
      console.log('Image-color association updated successfully');
    } catch (err) {
      setError('Greška pri vezivanju slike za boju: ' + err.message);
    }
  };


  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setDraggedIndex(index);
    
    // Add visual feedback to the dragged element
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    const draggedIndexFromData = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (draggedIndexFromData === dropIndex || draggedIndex === null) {
      setDraggedIndex(null);
      return;
    }

    try {
      setIsReordering(true);
      
      // Create new gallery array with reordered images
      const newGallery = [...editingProduct.gallery];
      const draggedImage = newGallery[draggedIndexFromData];
      
      // Remove dragged image
      newGallery.splice(draggedIndexFromData, 1);
      
      // Insert at new position
      newGallery.splice(dropIndex, 0, draggedImage);
      
      // Update local state immediately for visual feedback
      setEditingProduct(prev => ({
        ...prev,
        gallery: newGallery
      }));

      // Save new order to backend
      const token = localStorage.getItem('adminToken');
      const response = await productService.reorderGalleryImages(
        editingProduct._id,
        newGallery.map((img, idx) => ({ 
          imageUrl: img.url, 
          newPosition: idx,
          colorAssociation: img.colorAssociation 
        })),
        token
      );

      // Update with response from server to ensure consistency
      if (response.success) {
        setEditingProduct(response.data);
        console.log('Image order updated successfully');
      }
      
    } catch (err) {
      setError('Greška pri ažuriranju redosleda slika: ' + err.message);
      // Revert local changes on error
      fetchProducts();
    } finally {
      setIsReordering(false);
      setDraggedIndex(null);
    }
  };

  const handleDragEnd = (e) => {
    // Reset visual feedback
    e.target.style.opacity = '1';
    setDraggedIndex(null);
  };

  const handleDeleteImage = async (imageIndex) => {
    if (!editingProduct || !window.confirm('Da li ste sigurni da želite da obrišete ovu sliku?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await productService.deleteImage(editingProduct._id, imageIndex, token);
      
      // Update local state
      const updatedGallery = [...editingProduct.gallery];
      updatedGallery.splice(imageIndex, 1);
      
      setEditingProduct(prev => ({
        ...prev,
        gallery: updatedGallery
      }));

      console.log('Image deleted successfully');
    } catch (err) {
      setError('Greška pri brisanju slike: ' + err.message);
    }
  };

  return (
    <div className="product-manager">
      <div className="product-manager-content">
        <div className="product-manager-header">
        <h2>Upravljanje aluminijumskim sistemima</h2>
        <button className="close-btn" onClick={onClose} title="Zatvori">
          <X size={24} />
        </button>
      </div>

      <div className="product-manager-tabs">
        <button 
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => { setActiveTab('list'); resetForm(); }}
        >
          Lista sistema
        </button>
        <button 
          className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => { setActiveTab('add'); resetForm(); }}
        >
          {editingProduct ? 'Uredi sistem' : 'Dodaj novi sistem'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {activeTab === 'list' && (
        <div className="products-list">
          {/* Filter Toggle */}
          <div className="products-list-controls">
            <label className="show-hidden-toggle">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
              />
              Prikaži sakrivene proizvode
            </label>
          </div>

          {loading ? (
            <div className="loading-state">Učitavanje...</div>
          ) : (
            <div className="products-grid">
              {products.map(product => (
                <div key={product._id} className={`product-item ${product.isHidden ? 'hidden-product' : ''}`}>
                  <div className="product-image">
                    <img 
                      src={product.gallery?.[0]?.url || '/images/placeholder/product-placeholder.jpg'} 
                      alt={safeRender(product.title, 'SR')}
                    />
                    {product.isHidden && (
                      <div className="hidden-overlay">
                        <span>SAKRIVENO</span>
                      </div>
                    )}
                  </div>
                  <div className="product-info">
                    <h4>{safeRender(product.title, 'SR')}</h4>
                    <p>{safeRender(product.catalog?.category, 'SR')}</p>
                    <p>Br: {product.catalog.catalogNumber}</p>
                    <div className="product-status">
                      {product.isHidden ? (
                        <span className="status-hidden">Sakriveno sa sajta</span>
                      ) : (
                        <span className="status-visible">Prikazano na sajtu</span>
                      )}
                    </div>
                  </div>

                  {/* Catalog Management */}
                  <div className="catalog-management">
                    <div className="catalog-status">
                      {product.catalogPdf?.publicId ? (
                        <div className="catalog-exists">
                          <span>📋 Katalog: {product.catalogPdf.filename}</span>
                          <div className="catalog-actions">
                            <a 
                              href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/products/${product._id}/catalog/download`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="view-catalog-btn"
                            >
                              Prikaži
                            </a>
                            <button 
                              onClick={() => handleCatalogDelete(product._id)}
                              className="delete-catalog-btn"
                            >
                              Obriši
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="catalog-upload">
                          <span>📋 Nema kataloga</span>
                          <p className="catalog-size-limit">Max veličina: 10MB</p>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                const file = e.target.files[0];
                                const fileSizeMB = file.size / (1024 * 1024);
                                if (fileSizeMB > 10) {
                                  alert(`PDF fajl je prevelik (${fileSizeMB.toFixed(2)} MB). Maksimalna veličina je 10MB. Molimo kompresujte PDF pre otpreme.`);
                                  e.target.value = ''; // Reset file input
                                  return;
                                }
                                handleCatalogUpload(product._id, file);
                              }
                            }}
                            disabled={uploadingCatalog === product._id}
                            id={`catalog-${product._id}`}
                            style={{ display: 'none' }}
                          />
                          <label 
                            htmlFor={`catalog-${product._id}`}
                            className={`upload-catalog-btn ${uploadingCatalog === product._id ? 'uploading' : ''}`}
                          >
                            {uploadingCatalog === product._id ? 'Otprema...' : 'Otpremi PDF'}
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="product-actions">
                    <button onClick={() => handleEdit(product)} className="edit-btn">
                      Uredi
                    </button>
                    
                    <button 
                      onClick={() => handleTranslateProduct(product._id)} 
                      className="translate-btn"
                      disabled={translatingProducts.has(product._id)}
                      title="Prevedi na drugi jezici pomoću AI"
                    >
                      {translatingProducts.has(product._id) ? (
                        <>
                          <Loader2 size={16} className="spinning" />
                          Prevodi...
                        </>
                      ) : (
                        <>
                          <Languages size={16} />
                          AI prevod
                        </>
                      )}
                    </button>
                    
                    {product.isHidden ? (
                      <button 
                        onClick={() => handleShowProduct(product._id)} 
                        className="show-btn"
                      >
                        Vrati
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleHideProduct(product._id)} 
                        className="hide-btn"
                      >
                        Sakrij
                      </button>
                    )}
                    
                    <button onClick={() => handleDelete(product._id)} className="delete-btn">
                      Obriši
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'add' && (
        <form className="product-form" onSubmit={handleSubmit}>
          {/* Language Switcher */}
          <div className="language-switcher">
            <div className="language-switcher-header">
              <h4>Jezik editovanja</h4>
              <p>Izaberite jezik za unos informacija o sistemu</p>
            </div>
            <div className="language-buttons">
              <button 
                type="button" 
                className={`language-btn ${currentLanguage === 'SR' ? 'active' : ''}`}
                onClick={() => setCurrentLanguage('SR')}
              >
                <Globe size={16} /> Srpski
              </button>
              <button 
                type="button" 
                className={`language-btn ${currentLanguage === 'EN' ? 'active' : ''}`}
                onClick={() => setCurrentLanguage('EN')}
              >
                <Globe size={16} /> English
              </button>
              <button 
                type="button" 
                className={`language-btn ${currentLanguage === 'DE' ? 'active' : ''}`}
                onClick={() => setCurrentLanguage('DE')}
              >
                <Globe size={16} /> Deutsch
              </button>
            </div>
          </div>

          {/* Basic Info */}
          <div className="form-section">
            <h3><FileText size={20} /> Osnovne informacije</h3>
            <div className="form-subsection">
              <h4 className="form-subsection-title">Naziv i identifikacija</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Naziv sistema *</label>
                  <input
                    type="text"
                    value={formData.title[currentLanguage.toLowerCase()] || ''}
                    onChange={(e) => handleMultilingualInputChange('title', e.target.value)}
                    placeholder={`Unesite naziv na ${currentLanguage === 'SR' ? 'srpskom' : currentLanguage === 'EN' ? 'engleskom' : 'nemačkom'} jeziku`}
                    required
                  />
                  <span className="form-help">Ovaj naziv će biti prikazan na stranici proizvoda</span>
                </div>
                
                <div className="form-group">
                  <label>Kataloski broj *</label>
                  <input
                    type="text"
                    value={formData.catalog.catalogNumber}
                    onChange={(e) => handleInputChange('catalog.catalogNumber', e.target.value)}
                    placeholder="Npr. ALU-001"
                    required
                  />
                  <span className="form-help">Jedinstven identifikator za sistem</span>
                </div>
              </div>
            </div>


            <div className="form-subsection">
              <h4 className="form-subsection-title">Opis i tagovi</h4>
              <div className="form-group">
                <label>Opis sistema *</label>
                <textarea
                  value={formData.description[currentLanguage.toLowerCase()] || ''}
                  onChange={(e) => handleMultilingualInputChange('description', e.target.value)}
                  placeholder={`Detaljni opis na ${currentLanguage === 'SR' ? 'srpskom' : currentLanguage === 'EN' ? 'engleskom' : 'nemačkom'} jeziku...`}
                  rows="4"
                  required
                />
                <span className="form-help">Detaljan opis proizvoda koji će biti prikazan korisnicima</span>
              </div>
              
              <div className="form-group">
                <label>Tagovi (odvojeni zarezom)</label>
                <input
                  type="text"
                  value={formData.catalog.tags[currentLanguage.toLowerCase()]?.join(', ') || ''}
                  onChange={handleTagsChange}
                  placeholder="aluminum, prozor, modern"
                />
                <span className="form-help">Ključne reči za lakše pronalaženje proizvoda</span>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3><Image size={20} /> Galerija slika</h3>
            <div className="form-subsection">
              <h4 className="form-subsection-title">Upload novih slika</h4>
              <div className="form-group">
                <label>Izaberite slike sistema</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                />
                <span className="form-help">Podržani formati: JPG, PNG, WebP. Maksimalna veličina: 5MB po slici. Prva slika će biti glavna.</span>
              </div>
            </div>

            {/* Show existing images with drag & drop reordering and color association */}
            {editingProduct && editingProduct.gallery && editingProduct.gallery.length > 0 && (
              <div className="existing-images">
                <h4>Postojeće slike</h4>
                <p className="reorder-instruction">
                  Prevucite slike da promenite redosled prikazivanja u galeriji
                </p>
                <div className={`images-grid-sortable ${isReordering ? 'loading' : ''}`}>
                  {editingProduct.gallery.map((image, index) => (
                    <div 
                      key={`image-${index}`}
                      className={`image-item-sortable ${draggedIndex === index ? 'dragging' : ''} ${isReordering ? 'reordering' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="drag-handle">
                        <GripVertical size={20} />
                      </div>
                      <div className="image-preview">
                        <img src={image.url} alt={image.alt || 'Product image'} />
                        <div className="image-position">#{index + 1}</div>
                      </div>
                      <div className="image-controls">
                        <div className="color-association">
                          <label>Vezuj za boju:</label>
                          <select
                            value={image.colorAssociation || ''}
                            onChange={(e) => handleImageColorAssociation(index, e.target.value)}
                            className="color-select"
                          >
                            <option value="">Generička slika</option>
                            {formData.colors
                              .filter(color => !image.categoryAssociation || color.category === image.categoryAssociation)
                              .map((color, index) => (
                              <option key={`color-${index}-${color.name}`} value={safeRender(color.name, 'SR')}>
                                {safeRender(color.name, 'SR')} ({color.category})
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="delete-image-btn"
                          onClick={() => handleDeleteImage(index)}
                          title="Obriši sliku"
                        >
<Trash2 size={16} />
                        </button>
                      </div>
                      {image.colorAssociation && (
                        <div className="color-indicator">
                          <span 
                            className="color-dot"
                            style={{ 
                              backgroundColor: formData.colors.find(c => safeRender(c.name, 'SR') === image.colorAssociation)?.hexCode || '#000' 
                            }}
                          ></span>
                          <span>{safeRender(image.colorAssociation, 'SR')}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Colors */}
          <div className="form-section">
            <h3><Palette size={20} /> Dostupne boje sistema</h3>
            {editingColorIndex !== null && (
              <div className="editing-indicator">
                <span><Edit size={16} /> Uređuješ boju #{editingColorIndex + 1}</span>
                <button type="button" onClick={cancelEditColor} className="cancel-btn">Odustani</button>
              </div>
            )}
            <div className="color-input-group">
              <div className="multilingual-inputs">
                <div className="language-input">
                  <label>Srpski:</label>
                  <input
                    type="text"
                    placeholder="Naziv boje (srpski)"
                    value={currentColor.name.sr}
                    onChange={(e) => setCurrentColor(prev => ({ 
                      ...prev, 
                      name: { ...prev.name, sr: e.target.value } 
                    }))}
                  />
                </div>
                <div className="language-input">
                  <label>English:</label>
                  <input
                    type="text"
                    placeholder="Color name (english)"
                    value={currentColor.name.en}
                    onChange={(e) => setCurrentColor(prev => ({ 
                      ...prev, 
                      name: { ...prev.name, en: e.target.value } 
                    }))}
                  />
                </div>
                <div className="language-input">
                  <label>Deutsch:</label>
                  <input
                    type="text"
                    placeholder="Farbe name (deutsch)"
                    value={currentColor.name.de}
                    onChange={(e) => setCurrentColor(prev => ({ 
                      ...prev, 
                      name: { ...prev.name, de: e.target.value } 
                    }))}
                  />
                </div>
              </div>
              <div className="color-controls">
                <input
                  type="color"
                  value={currentColor.hexCode}
                  onChange={(e) => setCurrentColor(prev => ({ ...prev, hexCode: e.target.value }))}
                />
                <select
                  value={currentColor.category}
                  onChange={(e) => setCurrentColor(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="Aloksaza">Aloksaza</option>
                  <option value="Plastifikacija">Plastifikacija</option>
                </select>
                <button type="button" onClick={addColor} className="add-btn">
                  {editingColorIndex !== null ? 'Ažuriraj' : 'Dodaj'}
                </button>
              </div>
            </div>
            
            {/* Display colors by category */}
            <div className="colors-by-category">
              <div className="color-category">
                <h4>Aloksaza boje</h4>
                <div className="added-items">
                  {formData.colors.filter(color => color.category === 'Aloksaza').map((color, index) => {
                    const originalIndex = formData.colors.findIndex(c => c === color);
                    return (
                      <div key={originalIndex} className={`color-item ${editingColorIndex === originalIndex ? 'editing' : ''}`}>
                        <span
                          className="color-preview"
                          style={{ backgroundColor: color.hexCode }}
                        ></span>
                        <span>{safeRender(color.name, 'SR')}</span>
                        <span className="color-category-badge">Aloksaza</span>
                        <div className="color-actions">
                          <button type="button" onClick={() => editColor(originalIndex)} className="edit-btn" title="Uredi boju">
                            <Edit size={14} />
                          </button>
                          <button type="button" onClick={() => removeColor(originalIndex)} className="remove-btn" title="Obriši boju">
                            <Minus size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="color-category">
                <h4>Plastifikacija boje</h4>
                <div className="added-items">
                  {formData.colors.filter(color => color.category === 'Plastifikacija').map((color, index) => {
                    const originalIndex = formData.colors.findIndex(c => c === color);
                    return (
                      <div key={originalIndex} className={`color-item ${editingColorIndex === originalIndex ? 'editing' : ''}`}>
                        <span
                          className="color-preview"
                          style={{ backgroundColor: color.hexCode }}
                        ></span>
                        <span>{safeRender(color.name, 'SR')}</span>
                        <span className="color-category-badge">Plastifikacija</span>
                        <div className="color-actions">
                          <button type="button" onClick={() => editColor(originalIndex)} className="edit-btn" title="Uredi boju">
                            <Edit size={14} />
                          </button>
                          <button type="button" onClick={() => removeColor(originalIndex)} className="remove-btn" title="Obriši boju">
                            <Minus size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Plastification Types */}
          <div className="form-section">
            <h3><Sparkles size={20} /> Završna obrada plastifikacije</h3>
            <div className="plastification-options">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.plastificationTypes.sjajna}
                    onChange={(e) => handleInputChange('plastificationTypes.sjajna', e.target.checked)}
                  />
                  Sjajna
                </label>
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.plastificationTypes.matt}
                    onChange={(e) => handleInputChange('plastificationTypes.matt', e.target.checked)}
                  />
                  Matt
                </label>
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.plastificationTypes.strukturalna}
                    onChange={(e) => handleInputChange('plastificationTypes.strukturalna', e.target.checked)}
                  />
                  Strukturalna
                </label>
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.plastificationTypes.showOnProduct}
                    onChange={(e) => handleInputChange('plastificationTypes.showOnProduct', e.target.checked)}
                  />
                  Prikaži na stranici proizvoda
                </label>
              </div>
            </div>
          </div>

          {/* Sizes */}
          <div className="form-section">
            <h3><Ruler size={20} /> Dostupne veličine</h3>
            <div className="size-input-group">
              <input
                type="text"
                placeholder="Naziv veličine"
                value={currentSize.name}
                onChange={(e) => setCurrentSize(prev => ({ ...prev, name: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Kod"
                value={currentSize.code}
                onChange={(e) => setCurrentSize(prev => ({ ...prev, code: e.target.value }))}
              />
              <button type="button" onClick={addSize} className="add-btn">Dodaj</button>
            </div>
            
            <div className="added-items">
              {formData.sizes.map((size, index) => (
                <div key={index} className="size-item">
                  <span>{safeRender(size.name, 'SR')} ({size.code})</span>
                  <button type="button" onClick={() => removeSize(index)} className="remove-btn" title="Obriši veličinu">
                    <Minus size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Price and Availability */}
          <div className="form-section">
            <h3><DollarSign size={20} /> Cena i dostupnost</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Cena *</label>
                <input
                  type="number"
                  value={formData.price.amount}
                  onChange={(e) => handleInputChange('price.amount', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Valuta</label>
                <select
                  value={formData.price.currency}
                  onChange={(e) => handleInputChange('price.currency', e.target.value)}
                >
                  <option value="RSD">RSD</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Količina na stanju</label>
                <input
                  type="number"
                  value={formData.availability.quantity}
                  onChange={(e) => handleInputChange('availability.quantity', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.availability.inStock}
                    onChange={(e) => handleInputChange('availability.inStock', e.target.checked)}
                  />
                  Dostupno na stanju
                </label>
              </div>
            </div>
          </div>

          {/* Measurement Groups */}
          <div className="form-section">
            <h3><Triangle size={20} /> Mere i dimenzije (grupisane)</h3>
            
            {/* Group Title Input */}
            <div className="measurement-group-input">
              <div className="group-title-input">
                <label>Naziv grupe mera ({currentLanguage}):</label>
                <input
                  type="text"
                  placeholder={`${currentLanguage === 'SR' ? 'npr. Profil 1, Osnovne mere, Termičke karakteristike' : currentLanguage === 'EN' ? 'e.g. Profile 1, Basic measurements, Thermal characteristics' : 'z.B. Profil 1, Grundmaße, Thermische Eigenschaften'}`}
                  value={currentMeasurementGroup.groupTitle[currentLanguage.toLowerCase()]}
                  onChange={(e) => setCurrentMeasurementGroup(prev => ({ 
                    ...prev, 
                    groupTitle: { 
                      ...prev.groupTitle, 
                      [currentLanguage.toLowerCase()]: e.target.value 
                    } 
                  }))}
                />
              </div>
              <button type="button" onClick={addMeasurementGroup} className="add-btn">
                {editingGroupIndex !== null ? 'Ažuriraj grupu' : 'Dodaj grupu'}
              </button>
              {editingGroupIndex !== null && (
                <button type="button" onClick={cancelEditMeasurementGroup} className="cancel-btn">Odustani</button>
              )}
            </div>

            {/* Display existing groups */}
            <div className="measurement-groups">
              {formData.measurementGroups.map((group, groupIndex) => (
                <div key={groupIndex} className={`measurement-group ${editingGroupIndex === groupIndex ? 'editing' : ''}`}>
                  <div className="group-header">
                    <h4>{safeRender(group.groupTitle, 'SR') || 'Bez naziva'}</h4>
                    <div className="group-actions">
                      <button type="button" onClick={() => editMeasurementGroup(groupIndex)} className="edit-btn" title="Uredi grupu">
                        <Edit size={14} />
                      </button>
                      <button type="button" onClick={() => removeMeasurementGroup(groupIndex)} className="remove-btn" title="Obriši grupu">
                        <Minus size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Individual measurements in group */}
                  {activeGroupForMeasurement === groupIndex && (
                    <div className="measurement-input-section">
                      {editingMeasurementIndex !== null && (
                        <div className="editing-indicator">
                          <span><Edit size={16} /> Uređuješ meru #{editingMeasurementIndex + 1} u grupi</span>
                          <button type="button" onClick={cancelEditMeasurement} className="cancel-btn">Odustani</button>
                        </div>
                      )}
                      
                      <div className="measurement-input-group">
                        <div className="measurement-name-input">
                          <label>Naziv mere ({currentLanguage}):</label>
                          <input
                            type="text"
                            placeholder={`${currentLanguage === 'SR' ? 'npr. Širina, Visina, Težina' : currentLanguage === 'EN' ? 'e.g. Width, Height, Weight' : 'z.B. Breite, Höhe, Gewicht'}`}
                            value={currentMeasurement.name[currentLanguage.toLowerCase()]}
                            onChange={(e) => setCurrentMeasurement(prev => ({ 
                              ...prev, 
                              name: { 
                                ...prev.name, 
                                [currentLanguage.toLowerCase()]: e.target.value 
                              } 
                            }))}
                          />
                        </div>
                        
                        <div className="measurement-value-input">
                          <label>Vrednost:</label>
                          <input
                            type="text"
                            placeholder="npr. 50 mm, 2.1 kg, 1.4 W/m²K"
                            value={currentMeasurement.value}
                            onChange={(e) => setCurrentMeasurement(prev => ({ 
                              ...prev, 
                              value: e.target.value 
                            }))}
                          />
                        </div>
                        
                        <button type="button" onClick={() => addMeasurementToGroup(groupIndex)} className="add-btn">
                          {editingMeasurementIndex !== null ? 'Ažuriraj' : 'Dodaj'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show measurements in group */}
                  <div className="group-measurements">
                    {group.measurements.map((measurement, measurementIndex) => (
                      <div key={measurementIndex} className={`measurement-item ${editingMeasurementIndex === measurementIndex && activeGroupForMeasurement === groupIndex ? 'editing' : ''}`}>
                        <span>
                          <strong>{safeRender(measurement.name, 'SR') || 'Bez naziva'}:</strong> {
                            typeof measurement.value === 'object' ? 
                              safeRender(measurement.value, 'SR') || 'Bez vrednosti' : 
                              measurement.value || 'Bez vrednosti'
                          }
                        </span>
                        <div className="measurement-actions">
                          <button type="button" onClick={() => editMeasurementInGroup(groupIndex, measurementIndex)} className="edit-btn" title="Uredi meru">
                            <Edit size={14} />
                          </button>
                          <button type="button" onClick={() => removeMeasurementFromGroup(groupIndex, measurementIndex)} className="remove-btn" title="Obriši meru">
                            <Minus size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {group.measurements.length === 0 && (
                      <p className="no-measurements">Nema mera u ovoj grupi</p>
                    )}
                    
                    {activeGroupForMeasurement !== groupIndex && (
                      <button 
                        type="button" 
                        onClick={() => setActiveGroupForMeasurement(groupIndex)} 
                        className="add-measurement-btn"
                      >
                        + Dodaj meru u grupu
                      </button>
                    )}
                    
                    {activeGroupForMeasurement === groupIndex && (
                      <button 
                        type="button" 
                        onClick={() => setActiveGroupForMeasurement(null)} 
                        className="close-measurement-btn"
                      >
                        Zatvori dodavanje mera
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {formData.measurementGroups.length === 0 && (
              <p className="no-groups">Nema kreiranih grupa mera. Dodajte prvu grupu gore.</p>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={() => { setActiveTab('list'); resetForm(); }} className="cancel-btn">
              Otkaži
            </button>
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Snimanje...' : (editingProduct ? 'Ažuriraj sistem' : 'Dodaj sistem')}
            </button>
          </div>
        </form>
      )}
      </div>
    </div>
  );
};

export default ProductManager;